import { supabase } from './supabase';
import type { AuctionChargeStatus } from './types';

/**
 * Admin-only Sales Room payment helpers. Reads go through Supabase (admin RLS
 * lets staff read all auction_charges / member_payment_methods); the charge +
 * refund actions hit Cloudflare Pages Functions that re-verify the caller's
 * role and own all money movement server-side.
 */

export interface SaleroomChargeRow {
  post_id: string;
  lot_number: string;
  lot_name: string;
  post_title: string | null;
  post_slug: string | null;
  ends_at: string;
  /** Total to charge: ledger amount when a charge exists, else hammer + flat postage. */
  amount_cents: number;
  /** Flat postage in cents (null = unspecified, 0 = free postage). */
  shipping_cost: number | null;
  winner_id: string;
  winner_name: string | null;
  winner_handle: string | null;
  winner_has_card: boolean;
  /** Ledger status, or null when no charge has been started yet. */
  charge_status: AuctionChargeStatus | null;
  charge_id: string | null;
  last_error: string | null;
  created_at: string | null;
}

async function authedFetch(path: string, init?: RequestInit) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}`, ...(init?.headers ?? {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error ?? `Request failed (${res.status}).`);
  return data as any;
}

/**
 * Every ended auction that has a winner, joined to its charge ledger row (if
 * any), the winner's identity, and whether they still hold a card. Drives the
 * admin "confirm winner & charge" dashboard.
 */
export async function fetchSaleroomCharges(): Promise<SaleroomChargeRow[]> {
  const { data: auctions, error } = await supabase
    .from('post_auctions_with_state')
    .select('post_id, lot_number, lot_name, ends_at, current_high_amount, current_high_bidder_id, shipping_cost')
    .eq('state', 'ended')
    .not('current_high_bidder_id', 'is', null)
    .order('ends_at', { ascending: false });
  if (error) throw error;

  const rows = (auctions ?? []).filter((a: any) => a.current_high_amount > 0);
  if (rows.length === 0) return [];

  const postIds = rows.map((a: any) => a.post_id);
  const winnerIds = Array.from(new Set(rows.map((a: any) => a.current_high_bidder_id as string)));

  const [postsRes, winnersRes, chargesRes, cardsRes] = await Promise.all([
    supabase.from('posts').select('id, title, slug').in('id', postIds),
    supabase.from('profiles').select('id, display_name, handle').in('id', winnerIds),
    supabase.from('auction_charges')
      .select('id, post_id, status, amount_cents, last_error, created_at')
      .in('post_id', postIds)
      .order('created_at', { ascending: false }),
    supabase.from('member_payment_methods')
      .select('user_id, has_card_on_file')
      .in('user_id', winnerIds),
  ]);
  if (postsRes.error) throw postsRes.error;
  if (winnersRes.error) throw winnersRes.error;
  if (chargesRes.error) throw chargesRes.error;
  if (cardsRes.error) throw cardsRes.error;

  const posts = new Map((postsRes.data ?? []).map((p: any) => [p.id, p]));
  const winners = new Map((winnersRes.data ?? []).map((p: any) => [p.id, p]));
  const cards = new Map((cardsRes.data ?? []).map((c: any) => [c.user_id, !!c.has_card_on_file]));
  // Most-recent charge per post (the query is ordered desc, so first wins).
  const charges = new Map<string, any>();
  for (const c of chargesRes.data ?? []) if (!charges.has(c.post_id)) charges.set(c.post_id, c);

  return rows.map((a: any): SaleroomChargeRow => {
    const post = posts.get(a.post_id);
    const winner = winners.get(a.current_high_bidder_id);
    const charge = charges.get(a.post_id);
    return {
      post_id: a.post_id,
      lot_number: a.lot_number,
      lot_name: a.lot_name,
      post_title: post?.title ?? null,
      post_slug: post?.slug ?? null,
      ends_at: a.ends_at,
      // The ledger is the truth for what was actually captured; before any
      // charge exists, mirror the server's charge math (hammer + flat postage).
      amount_cents: charge?.amount_cents ?? (a.current_high_amount + (a.shipping_cost ?? 0)),
      shipping_cost: a.shipping_cost ?? null,
      winner_id: a.current_high_bidder_id,
      winner_name: winner?.display_name ?? null,
      winner_handle: winner?.handle ?? null,
      winner_has_card: cards.get(a.current_high_bidder_id) ?? false,
      charge_status: (charge?.status as AuctionChargeStatus) ?? null,
      charge_id: charge?.id ?? null,
      last_error: charge?.last_error ?? null,
      created_at: charge?.created_at ?? null,
    };
  });
}

/**
 * The four working buckets the admin Saleroom dashboard tabs by:
 *   • action   — still needs the team to charge or retry (incl. no charge yet)
 *   • progress — in flight: charging, processing, or waiting on member SCA
 *   • paid     — captured
 *   • refunded — returned
 */
export type SaleroomChargeGroup = 'action' | 'progress' | 'paid' | 'refunded';

export function saleroomChargeGroup(status: AuctionChargeStatus | null): SaleroomChargeGroup {
  switch (status) {
    case 'succeeded':
      return 'paid';
    case 'refunded':
      return 'refunded';
    case 'pending':
    case 'processing':
    case 'requires_action':
      return 'progress';
    default:
      // null (no charge started), pending_review, failed, canceled
      return 'action';
  }
}

/** Count of charges still needing team action — drives the nav-tab indicator. */
export function countOutstandingSaleroomCharges(rows: SaleroomChargeRow[]): number {
  return rows.reduce((n, r) => n + (saleroomChargeGroup(r.charge_status) === 'action' ? 1 : 0), 0);
}

/** Confirm the winner and fire the off-session charge. */
export async function chargeAuctionWinner(postId: string): Promise<{ ok: boolean; status: string; error?: string }> {
  return authedFetch(`/api/admin/auctions/${postId}/charge`, { method: 'POST', body: '{}' });
}

/** Refund a succeeded auction-win charge. */
export async function refundAuctionCharge(postId: string): Promise<{ ok: boolean; status: string }> {
  return authedFetch(`/api/admin/auctions/${postId}/refund`, { method: 'POST', body: '{}' });
}

// ---------- Live-lot bid management (admin) ------------------------------
// Admins can void an individual bid on a live lot; the lot then falls back to
// the next-highest remaining bid (post_auctions_with_state recomputes it).

export interface AdminLotBid {
  id: string;
  amount_cents: number;
  created_at: string;
  bidder_name: string | null;
  bidder_handle: string | null;
}

export interface AdminLiveLot {
  post_id: string;
  lot_number: string;
  lot_name: string;
  post_slug: string | null;
  ends_at: string;
  current_high_amount: number;
  bids: AdminLotBid[];   // highest first
}

/** Every currently-live lot with its full bid list (highest first), for the
 *  admin bid-management view. */
export async function fetchLiveLotsWithBids(): Promise<AdminLiveLot[]> {
  const { data: auctions, error } = await supabase
    .from('post_auctions_with_state')
    .select('post_id, lot_number, lot_name, ends_at, current_high_amount')
    .eq('state', 'live')
    .order('ends_at', { ascending: true });
  if (error) throw error;

  const lots = auctions ?? [];
  if (lots.length === 0) return [];
  const postIds = lots.map((a: any) => a.post_id);

  const [postsRes, bidsRes] = await Promise.all([
    supabase.from('posts').select('id, slug').in('id', postIds),
    supabase
      .from('auction_bids_public')
      .select('id, post_id, amount, created_at, bidder:profiles!auction_bids_bidder_id_fkey ( display_name, handle )')
      .in('post_id', postIds)
      .order('amount', { ascending: false }),
  ]);
  if (postsRes.error) throw postsRes.error;
  if (bidsRes.error) throw bidsRes.error;

  const slugs = new Map((postsRes.data ?? []).map((p: any) => [p.id, p.slug]));
  const bidsByPost = new Map<string, AdminLotBid[]>();
  for (const b of (bidsRes.data ?? []) as any[]) {
    const arr = bidsByPost.get(b.post_id) ?? [];
    arr.push({
      id: b.id,
      amount_cents: b.amount,
      created_at: b.created_at,
      bidder_name: b.bidder?.display_name ?? null,
      bidder_handle: b.bidder?.handle ?? null,
    });
    bidsByPost.set(b.post_id, arr);
  }

  return lots.map((a: any): AdminLiveLot => ({
    post_id: a.post_id,
    lot_number: a.lot_number,
    lot_name: a.lot_name,
    post_slug: slugs.get(a.post_id) ?? null,
    ends_at: a.ends_at,
    current_high_amount: a.current_high_amount,
    bids: bidsByPost.get(a.post_id) ?? [],
  }));
}

/** Void one bid on a live lot (admin/developer only). Throws on RPC error. */
export async function adminRemoveBid(bidId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_remove_bid', { target_bid_id: bidId });
  if (error) throw error;
}
